import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { apiCall } from '../config/api';
import { ToastContext } from '../App';

interface Payment {
  id: number;
  month: string;
  total_amount: number;
  status: 'paid' | 'pending';
  created_at: string;
  user_name?: string;
  user_email?: string;
  paid_at?: string;
}

interface PaymentDetail {
  date: string;
  start_time: string;
  end_time: string;
  animal_name: string;
}

interface UserStats {
  total_lessons: number;
  total_hours: number;
  total_animals: number;
  month: string;
}

const AccountPage: React.FC = () => {
  const { user, changePassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [payments, setPayments] = useState<Payment[]>([]);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetail[]>([]);
  const [loadingPaymentDetails, setLoadingPaymentDetails] = useState(false);
  const { showToast } = React.useContext(ToastContext);

  useEffect(() => {
    if (user) {
      loadUserPayments();
      loadUserStats();
    }
  }, [user]);

  const loadUserPayments = async () => {
    try {
      setLoadingPayments(true);
      const response = await apiCall('/payments/user');
      setPayments(response.payments || []);
    } catch (error) {
      console.error('Error loading user payments:', error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const loadUserStats = async () => {
    try {
      setLoadingStats(true);
      const response = await apiCall('/bookings/user-quick-stats');
      setUserStats(response);
    } catch (error) {
      console.error('Error loading user stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('As passwords não coincidem');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError('A password deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      const success = await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      if (success) {
        setSuccess('Password alterada com sucesso!');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordForm(false);
      } else {
        setError('Erro ao alterar password. Verifique a password atual.');
      }
    } catch (err) {
      setError('Erro ao comunicar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  const viewPaymentDetails = async (paymentId: number) => {
    try {
      setLoadingPaymentDetails(true);
      const response = await apiCall(`/payments/details?payment_id=${paymentId}`);
      setSelectedPayment(response.payment);
      setPaymentDetails(response.details || []);
      setShowPaymentModal(true);
    } catch (error) {
      console.error('Error loading payment details:', error);
    } finally {
      setLoadingPaymentDetails(false);
    }
  };

  const formatMonth = (monthString: string) => {
    const date = new Date(monthString + '-01');
    return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatTime = (timeString: string) => {
    return timeString.substring(0, 5); // Remove seconds if present
  };

  if (!user) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container py-4">
      {/* Cabeçalho roxo consistente */}
      <div className="card shadow mb-4">
        <div className="card-header" style={{ backgroundColor: '#a2449c', color: 'white', borderTopLeftRadius: '0.5rem', borderTopRightRadius: '0.5rem' }}>
          <h4 className="mb-0"><i className="fas fa-user me-2"></i>Minha Conta</h4>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <i className="fas fa-check-circle me-2"></i>
          {success}
        </div>
      )}

      <div className="row">
        <div className="col-md-6">
          {/* Informações do Utilizador */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-info-circle me-2"></i>
                Informações Pessoais
              </h5>
            </div>
            <div className="card-body personal-info-block">
              <div className="mt-3">
                <strong>Nome:</strong> {user.name}
              </div>
              <div className="mt-3">
                <strong>Email:</strong> {user.email}
              </div>
              <div className="mt-3">
                <strong>Tipo de Conta:</strong> {user.role === 'trainer' ? 'Treinador' : user.role === 'student' ? 'Aluno' : 'Convidado'}
              </div>
            </div>
          </div>

          {/* Alteração de Password */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-key me-2" style={{ color: '#a2449c' }}></i>
                Alterar Password
              </h5>
            </div>
            <div className="card-body">
              {!showPasswordForm ? (
                <div className="d-grid">
                  <button
                    className="btn mt-3"
                    style={{ backgroundColor: '#a2449c', color: 'white' }}
                    onClick={() => setShowPasswordForm(true)}
                  >
                    <i className="fas fa-edit me-2"></i>
                    Alterar Password
                  </button>
                </div>
              ) : (
                <form onSubmit={handlePasswordChange}>
                  <div className="mb-3">
                    <label htmlFor="currentPassword" className="form-label">Password Atual</label>
                    <input
                      type="password"
                      className="form-control"
                      id="currentPassword"
                      value={passwordForm.currentPassword}
                      onChange={e => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="newPassword" className="form-label">Nova Password</label>
                    <input
                      type="password"
                      className="form-control"
                      id="newPassword"
                      value={passwordForm.newPassword}
                      onChange={e => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="confirmPassword" className="form-label">Confirmar Nova Password</label>
                    <input
                      type="password"
                      className="form-control"
                      id="confirmPassword"
                      value={passwordForm.confirmPassword}
                      onChange={e => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                      required
                    />
                  </div>
                  <div className="d-grid gap-2">
                    <button
                      type="submit"
                      className="btn"
                      style={{ backgroundColor: '#a2449c', color: 'white' }}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          A alterar...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Guardar
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Histórico de Pagamentos */}
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-history me-2" style={{ color: '#a2449c' }}></i>
                Histórico de Pagamentos
              </h5>
            </div>
            <div className="card-body">
              {loadingPayments ? (
                <div className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">A carregar...</span>
                  </div>
                </div>
              ) : payments.length === 0 ? (
                <p className="text-center text-muted">Nenhum pagamento encontrado.</p>
              ) : (
                <div>
                  {payments.map((payment) => (
                    <div key={payment.id} className="card mb-3 border-0 shadow-sm">
                      <div className="card-body">
                        <div className="row align-items-center">
                          <div className="col-md-4">
                            <h6 className="mb-1" style={{ color: '#a2449c' }}>{formatMonth(payment.month)}</h6>
                            <small className="text-muted">Criado em {formatDate(payment.created_at)}</small>
                          </div>
                          <div className="col-md-3">
                            <strong style={{ color: payment.status === 'paid' ? '#198754' : '#deae26', fontWeight: 700 }}>{payment.total_amount}€</strong>
                          </div>
                          <div className="col-md-3">
                            <span style={{
                              backgroundColor: payment.status === 'paid' ? '#198754' : '#deae26',
                              color: payment.status === 'paid' ? 'white' : '#4a3c0a',
                              borderRadius: '0.5rem',
                              padding: '0.35em 0.8em',
                              fontWeight: 600,
                              fontSize: '1em',
                              display: 'inline-block'
                            }}>
                              {payment.status === 'paid' ? 'Pago' : 'Pendente'}
                            </span>
                          </div>
                          <div className="col-md-2 text-end">
                            <button 
                              className="btn btn-sm"
                              style={{ borderColor: '#a2449c', color: '#a2449c' }}
                              onClick={() => viewPaymentDetails(payment.id)}
                              disabled={loadingPaymentDetails}
                            >
                              <i className="fas fa-eye me-1"></i>Detalhes
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-6">
          {/* Estatísticas Rápidas */}
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-chart-bar me-2"></i>
                Estatísticas Rápidas
              </h5>
            </div>
            <div className="card-body">
              {loadingStats ? (
                <div className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">A carregar...</span>
                  </div>
                </div>
              ) : userStats ? (
                <div className="row text-center">
                  <div className="col-6 mb-3 mt-3">
                    <div className="border rounded p-3">
                      <i className="fas fa-calendar-check fa-2x mb-2" style={{ color: '#a2449c' }}></i>
                      <h4 className="mb-1">{userStats.total_lessons}</h4>
                      <small className="text-muted">Aulas Este Mês</small>
                    </div>
                  </div>
                  <div className="col-6 mb-3 mt-3">
                    <div className="border rounded p-3">
                      <i className="fas fa-paw fa-2x mb-2" style={{ color: '#7c3fa0' }}></i>
                      <h4 className="mb-1">{userStats.total_animals}</h4>
                      <small className="text-muted">Animais Registados</small>
                    </div>
                  </div>
                  <div className="col-6 mb-3">
                    <div className="border rounded p-3">
                      <i className="fas fa-clock fa-2x mb-2" style={{ color: '#b266b2' }}></i>
                      <h4 className="mb-1">{userStats.total_hours}</h4>
                      <small className="text-muted">Tempo Total</small>
                    </div>
                  </div>
                  <div className="col-6 mb-3">
                    <div className="border rounded p-3">
                      <i className="fas fa-calendar fa-2x mb-2" style={{ color: '#cfa2e6' }}></i>
                      <h4 className="mb-1">{formatMonth(userStats.month)}</h4>
                      <small className="text-muted">Período</small>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-center text-muted">Erro ao carregar estatísticas.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Detalhes do Pagamento */}
      {showPaymentModal && selectedPayment && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Detalhes do Pagamento</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowPaymentModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {loadingPaymentDetails ? (
                  <div className="text-center">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">A carregar...</span>
                    </div>
                  </div>
                ) : (
                  <div className="row">
                    <div className="col-md-6">
                      <h6>Informações do Pagamento</h6>
                      <table className="table table-sm">
                        <tbody>
                          {selectedPayment.user_name && (
                            <tr><td><strong>Aluno:</strong></td><td>{selectedPayment.user_name}</td></tr>
                          )}
                          {selectedPayment.user_email && (
                            <tr><td><strong>Email:</strong></td><td>{selectedPayment.user_email}</td></tr>
                          )}
                          <tr><td><strong>Mês:</strong></td><td>{formatMonth(selectedPayment.month)}</td></tr>
                          <tr><td><strong>Valor:</strong></td><td><span className="fw-bold text-success">{selectedPayment.total_amount}€</span></td></tr>
                          <tr><td><strong>Status:</strong></td><td>
                            <span className={`badge bg-${selectedPayment.status === 'paid' ? 'success' : 'warning'}`}>
                              {selectedPayment.status === 'paid' ? 'Pago' : 'Pendente'}
                            </span>
                          </td></tr>
                          <tr><td><strong>Criado em:</strong></td><td>{formatDate(selectedPayment.created_at)}</td></tr>
                          {selectedPayment.paid_at && (
                            <tr><td><strong>Pago em:</strong></td><td>{formatDate(selectedPayment.paid_at)}</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="col-md-6">
                      <h6>Aulas Incluídas ({paymentDetails.length})</h6>
                      {paymentDetails.length > 0 ? (
                        <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Data</th>
                                <th>Hora</th>
                                <th>Animal</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paymentDetails.map((detail, index) => (
                                <tr key={index}>
                                  <td>{formatDate(detail.date)}</td>
                                  <td>{formatTime(detail.start_time)} - {formatTime(detail.end_time)}</td>
                                  <td>{detail.animal_name}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-muted">Nenhuma aula encontrada</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowPaymentModal(false)}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountPage; 